import React, { useCallback, useEffect, useState } from 'react';
import { Button, Input, Label, Modal, ModalBody } from 'reactstrap';
import { aiWritePrompt, getGuardrails } from '../../helper/backend-methods';
import { showToast } from '../../components/Toast';
import { getDefaultDataCollection } from '../../utils/defaultDataCollection';
import '../../assets/css/--component-rubric.css';

/**
 * The wizard that writes a prompt from answers instead of a blank box.
 *
 * A modal over a dimmed page, deliberately: these questions are the whole task
 * while they are open, and the half-filled form behind them is a distraction
 * and an invitation to abandon it. One question per screen for the same
 * reason — six short screens get finished; one page of six fields gets left
 * halfway and saved as nothing.
 *
 * Backwards works everywhere, and nothing is lost going back, so a wrong
 * answer costs one click rather than a restart. Only the two questions that
 * cannot be answered later block Next.
 *
 * It saves nothing. The answers become a prompt, the prompt fills the
 * sectioned editor, and the customer saves it there with a voice.
 */

const TONES = [
  { id: 'warm', label: 'Warm and unhurried', note: 'Takes its time. Good when callers are worried.' },
  { id: 'brisk', label: 'Brisk and efficient', note: 'Gets to the point. Good for repeat, routine calls.' },
  { id: 'formal', label: 'Formal and precise', note: 'Careful wording. Good where accuracy matters.' },
  { id: 'casual', label: 'Relaxed and conversational', note: 'Everyday language, like a friendly colleague.' },
];

const WHEN_STUCK = [
  { id: 'message', label: 'Take a message and a number', note: 'Someone calls them back.' },
  { id: 'transfer', label: 'Put them through to a person', note: 'Needs someone available to answer.' },
  { id: 'callback', label: 'Arrange a callback', note: 'Books a time rather than leaving it open.' },
];

const SixQuestions = ({ open, company, website, type, onWritten, onCancel }) => {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({
    business: '', callers: '', goal: '', industry: '', guardrails: [], guardrailsOther: '',
    tone: 'warm', whenStuck: 'message',
  });
  const [extraFields, setExtraFields] = useState([]);
  const [catalogue, setCatalogue] = useState(null);
  const [writing, setWriting] = useState(false);

  const loadGuardrails = useCallback(async (industry) => {
    try {
      const response = await getGuardrails(industry);
      const data = response?.data?.data ?? response?.data;
      if (data?.universal) {
        setCatalogue(data);
        setAnswers((a) => ({
          ...a,
          // Keep anything already ticked, and add the call-handling rules,
          // which everyone needs. A business's own obligations stay unticked.
          guardrails: Array.from(new Set([
            ...a.guardrails.filter((id) => !(data.industry || []).some((g) => g.id === id)),
            ...data.universal.filter((g) => g.defaultOn).map((g) => g.id),
          ])),
        }));
      }
    } catch {
      setCatalogue({ universal: [], industry: [], industries: [] });
    }
  }, []);

  useEffect(() => { if (open) loadGuardrails(); }, [open, loadGuardrails]);

  const set = (field, value) => setAnswers((a) => ({ ...a, [field]: value }));
  const toggle = (id) =>
    setAnswers((a) => ({
      ...a,
      guardrails: a.guardrails.includes(id) ? a.guardrails.filter((g) => g !== id) : [...a.guardrails, id],
    }));

  const setField = (index, key, value) =>
    setExtraFields((fields) => fields.map((f, i) => (i === index ? { ...f, [key]: value } : f)));

  const write = async () => {
    setWriting(true);
    try {
      const response = await aiWritePrompt({ ...answers, company, type: type || 'inbound' });
      const data = response?.data?.data ?? response?.data;
      if (!response?.status || !data?.prompt) {
        // The answers are still here, so this is a retry rather than a loss.
        showToast('error', 'Could not write it', data?.message || 'Your answers are saved — please try again.');
        return;
      }
      onWritten({
        ...data,
        customDataCollection: extraFields.filter((f) => f.key.trim() && f.description.trim()),
      });
    } catch {
      showToast('error', 'Could not write it', 'Your answers are saved — please try again.');
    } finally {
      setWriting(false);
    }
  };

  const steps = [
    {
      title: 'What does your business do?',
      why: 'This becomes who the agent says it is, and what it knows about you.',
      hint: website ? `Filled in from ${website}. Change anything that is wrong.` : null,
      body: (
        <Input type="textarea" rows="4" className="custom-textarea" value={answers.business}
          onChange={(e) => set('business', e.target.value)}
          placeholder="A family dental practice in Reading. Check-ups, hygienist, emergency care. Open Monday to Saturday." />
      ),
      ready: () => answers.business.trim().length >= 10,
    },
    {
      title: 'Who calls you, and why?',
      why: 'Voice models behave differently when they know the caller is in pain, or in a hurry, or comparing prices.',
      body: (
        <Input type="textarea" rows="4" className="custom-textarea" value={answers.callers}
          onChange={(e) => set('callers', e.target.value)}
          placeholder="Patients booking check-ups, and people in pain wanting to be seen today." />
      ),
      ready: () => answers.callers.trim().length >= 5,
    },
    {
      title: 'What should the agent get done on a call?',
      why: 'These become the steps of the call, in order. The agent follows them rather than improvising.',
      hint: 'One step per line.',
      body: (
        <>
          <Input type="textarea" rows="4" className="custom-textarea" value={answers.goal}
            onChange={(e) => set('goal', e.target.value)}
            placeholder={'Find out what they need\nCheck whether it is urgent\nOffer the first two appointments that fit'} />
          <Label className="mt-3 mb-1 font-bold">What kind of business is it?</Label>
          <p className="wizard-hint">This decides which rules we offer you on the next screen.</p>
          <Input type="select" className="custom-input" value={answers.industry}
            onChange={(e) => { set('industry', e.target.value); loadGuardrails(e.target.value); }}>
            <option value="">Choose one…</option>
            {(catalogue?.industries || []).map((i) => <option key={i.id} value={i.id}>{i.label}</option>)}
          </Input>
        </>
      ),
      ready: () => answers.goal.trim().length >= 5,
    },
    {
      title: 'What must it never do?',
      why: 'Voice models weight this section hardest, which is why it is worth answering properly. The first group is how to behave on any phone call. The rest are for your kind of business.',
      body: (
        <>
          <p className="wizard-group">On any call</p>
          <div className="d-flex flex-wrap gap-2">
            {(catalogue?.universal || []).map((g) => (
              <Button key={g.id} size="sm" color={answers.guardrails.includes(g.id) ? 'primary' : 'light'}
                onClick={() => toggle(g.id)}>{g.label}{g.regulatory ? ' ·' : ''}</Button>
            ))}
          </div>
          {(catalogue?.industry || []).length > 0 && (
            <>
              <p className="wizard-group mt-3">For your kind of business</p>
              <div className="d-flex flex-wrap gap-2">
                {catalogue.industry.map((g) => (
                  <Button key={g.id} size="sm" color={answers.guardrails.includes(g.id) ? 'primary' : 'light'}
                    onClick={() => toggle(g.id)}>{g.label}{g.regulatory ? ' ·' : ''}</Button>
                ))}
              </div>
              <p className="wizard-hint mt-2">
                · marks a rule that exists because of the law rather than because it is sensible.
                None of these are ticked for you — we are not your compliance adviser, and only
                you know which apply.
              </p>
            </>
          )}
          <Label className="mt-3 mb-1 font-bold">Anything else, in your own words</Label>
          <Input type="textarea" rows="3" className="custom-textarea" value={answers.guardrailsOther}
            onChange={(e) => set('guardrailsOther', e.target.value)}
            placeholder="Never say a dentist is free without checking the diary" />
        </>
      ),
      ready: () => true,
    },
    {
      title: 'How should it sound?',
      why: 'Said once, here. Repeating it through the prompt makes models follow it less, not more.',
      body: (
        <div className="d-flex flex-column gap-2">
          {TONES.map((t) => (
            <button type="button" key={t.id}
              className={`wizard-choice ${answers.tone === t.id ? 'wizard-choice-on' : ''}`}
              onClick={() => set('tone', t.id)}>
              <strong>{t.label}</strong><span>{t.note}</span>
            </button>
          ))}
        </div>
      ),
      ready: () => true,
    },
    {
      title: 'What happens when it cannot help?',
      why: 'Without this an agent keeps trying, and a caller who cannot be helped stays on the line until they give up.',
      body: (
        <div className="d-flex flex-column gap-2">
          {WHEN_STUCK.map((w) => (
            <button type="button" key={w.id}
              className={`wizard-choice ${answers.whenStuck === w.id ? 'wizard-choice-on' : ''}`}
              onClick={() => set('whenStuck', w.id)}>
              <strong>{w.label}</strong><span>{w.note}</span>
            </button>
          ))}
        </div>
      ),
      ready: () => true,
    },
    {
      title: 'What should we record from each call?',
      why: 'After every call an AI reads the transcript and fills these in, so you can filter and count them later. Two are always collected. Add anything else you would want to search by.',
      body: (
        <>
          <p className="wizard-group">Always collected</p>
          {getDefaultDataCollection().map((field) => (
            <div className="wizard-field wizard-field-fixed" key={field.key}>
              <strong>{field.key}</strong>
              <span>{field.description}</span>
            </div>
          ))}

          <p className="wizard-group mt-3">Anything else</p>
          {extraFields.length === 0 && (
            <p className="wizard-hint">
              Nothing yet. Most people add one or two — “quoted_price”, “callback_requested”,
              “competitor_mentioned”.
            </p>
          )}
          {extraFields.map((field, index) => (
            <div className="wizard-field" key={index}>
              <div className="d-flex gap-2">
                <Input className="custom-input" placeholder="callback_requested" value={field.key}
                  onChange={(e) => setField(index, 'key', e.target.value.replace(/\s+/g, '_'))} />
                <Input type="select" className="custom-input wizard-field-type" value={field.type}
                  onChange={(e) => setField(index, 'type', e.target.value)}>
                  <option value="string">text</option>
                  <option value="boolean">yes or no</option>
                  <option value="number">a number</option>
                </Input>
                <Button color="light" onClick={() => setExtraFields((f) => f.filter((_, i) => i !== index))}>
                  Remove
                </Button>
              </div>
              <Input className="custom-input mt-2" value={field.description}
                onChange={(e) => setField(index, 'description', e.target.value)}
                placeholder="How to decide it — e.g. true when the caller asked to be rung back" />
            </div>
          ))}
          <Button color="light" className="mt-2"
            onClick={() => setExtraFields((f) => [...f, { key: '', type: 'string', description: '' }])}>
            Add something to record
          </Button>
        </>
      ),
      ready: () => extraFields.every((f) => !f.key.trim() || f.description.trim()),
      readyNote: 'Say how to decide each one, or remove it.',
    },
  ];

  const current = steps[step];
  const last = step === steps.length - 1;

  return (
    <Modal isOpen={open} toggle={writing ? undefined : onCancel} centered size="lg" scrollable backdrop="static">
      <ModalBody className="p-4">
        {writing ? (
          <div className="wizard-writing">
            <h5>Writing your prompt</h5>
            <p className="wizard-hint">
              About twenty seconds. We are putting your answers into the sections a voice model
              follows best — who the agent is, who calls, how it sounds, what to do, and what it
              must never do.
            </p>
            <div className="wizard-bar"><span /></div>
          </div>
        ) : (
          <>
            <div className="wizard-progress">
              {steps.map((_, i) => (
                <button type="button" key={i}
                  className={i <= step ? 'done' : ''}
                  // Backwards by clicking a step you have already answered.
                  onClick={() => i < step && setStep(i)}
                  aria-label={`Step ${i + 1}`} />
              ))}
              <small>{step + 1} of {steps.length}</small>
            </div>

            <h5 className="mt-3 mb-1">{current.title}</h5>
            <p className="wizard-why">{current.why}</p>
            {current.hint && <p className="wizard-hint">{current.hint}</p>}
            <div className="mt-3">{current.body}</div>

            <div className="d-flex justify-content-between align-items-center mt-4">
              <Button color="link" className="p-0" onClick={() => (step === 0 ? onCancel() : setStep(step - 1))}>
                {step === 0 ? 'Cancel' : '← Back'}
              </Button>
              <div className="d-flex align-items-center gap-3">
                {!current.ready() && current.readyNote && (
                  <small className="text-muted">{current.readyNote}</small>
                )}
                <Button color="primary" disabled={!current.ready()}
                  onClick={() => (last ? write() : setStep(step + 1))}>
                  {last ? 'Write my prompt' : 'Next'}
                </Button>
              </div>
            </div>
          </>
        )}
      </ModalBody>
    </Modal>
  );
};

export default SixQuestions;
